import React, { useState, useEffect } from 'react';
import Icon from './AppIcon';
import { taskCompletionPhotosService } from '../services/taskCompletionPhotosService';
import { useLanguage } from '../contexts/LanguageContext';

const CATEGORY_COLORS = {
  Safety: 'bg-red-100 text-red-700 border-red-200',
  Quality: 'bg-blue-100 text-blue-700 border-blue-200',
  Operations: 'bg-amber-100 text-amber-700 border-amber-200',
  Documentation: 'bg-purple-100 text-purple-700 border-purple-200',
};

const TaskDetailModal = ({ task, onClose }) => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [photoError, setPhotoError] = useState('');
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const { t } = useLanguage();

  const siteName = task?.site?.site_name || '';
  const completedByName = task?.completed_by_user?.full_name || 'Unknown';
  const completedAt = task?.completed_at ? new Date(task?.completed_at) : null;

  useEffect(() => {
    if (!task?.id) return;
    const load = async () => {
      setLoading(true);
      const { data, error: err } = await taskCompletionPhotosService?.getPhotosForTask(task?.id);
      if (err) {
        setPhotoError(err?.code === 'PGRST301'
          ? `Permission denied: ${err?.message}`
          : `Failed to load photos: ${err?.message || 'Unknown error'}`);
      } else {
        setPhotos(data || []);
      }
      setLoading(false);
    };
    load();
  }, [task?.id]);

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-4" onClick={onClose}>
        <div className="bg-card rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-lg max-h-[85vh] sm:max-h-[80vh] flex flex-col" onClick={e => e?.stopPropagation()}>
          {/* Header */}
          <div className="px-5 py-4 border-b border-border flex items-center justify-between flex-shrink-0">
            <div className="min-w-0">
              <h3 className="font-semibold text-foreground truncate">{task?.title}</h3>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${CATEGORY_COLORS?.[task?.category]}`}>{task?.category}</span>
                {siteName && <span className="text-xs text-muted-foreground">{siteName}</span>}
                {task?.date && <span className="text-xs text-muted-foreground">{new Date(task?.date + 'T00:00:00')?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
              </div>
            </div>
            <button onClick={onClose} className="w-10 h-10 rounded-lg hover:bg-muted flex items-center justify-center flex-shrink-0">
              <Icon name="X" size={18} />
            </button>
          </div>

          {/* Body */}
          <div className="px-5 py-4 overflow-y-auto flex-1 space-y-4">
            {/* Completion info */}
            {task?.status === 'completed' && completedAt && (
              <div className="flex items-start gap-3 p-3 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 rounded-lg">
                <Icon name="CheckCircle2" size={18} className="text-emerald-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-300">
                    Completed by {completedByName}
                  </p>
                  {task?.work_carried_out_by && (
                    <p className="text-sm font-medium text-emerald-800 dark:text-emerald-300 mt-0.5">
                      Work carried out by {task?.work_carried_out_by}
                    </p>
                  )}
                  <p className="text-xs text-emerald-700 dark:text-emerald-400 mt-0.5">
                    {completedAt?.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })} at {completedAt?.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            )}

            {task?.status === 'blocked' && (
              <div className="flex items-start gap-3 p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg">
                <Icon name="Ban" size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm font-semibold text-red-800 dark:text-red-300">Blocked</p>
              </div>
            )}

            {/* Description (admin-entered task instructions) */}
            {task?.description && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Description</p>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-sm text-foreground whitespace-pre-wrap">{task?.description}</p>
                </div>
              </div>
            )}

            {/* Notes */}
            {task?.notes && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Notes</p>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-sm text-foreground whitespace-pre-wrap">{task?.notes}</p>
                </div>
              </div>
            )}

            {/* Task details */}
            <div className="flex items-center gap-3 flex-wrap text-xs text-muted-foreground">
              {task?.required && <span className="text-red-600 font-medium">Mandatory</span>}
              {task?.photo_required && <span className="text-amber-600 font-medium flex items-center gap-1"><Icon name="Camera" size={11} />{task?.min_photos > 1 ? `${task?.min_photos} Photos Required` : 'Photo Required'}</span>}
              {task?.due_time && <span className="flex items-center gap-1"><Icon name="Clock" size={11} />Due {task?.due_time}</span>}
            </div>

            {/* Photo gallery */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Photo Evidence</p>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-accent" />
                </div>
              ) : photoError ? (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                  <Icon name="AlertCircle" size={14} className="text-red-500" />
                  <p className="text-xs text-red-700">{photoError}</p>
                </div>
              ) : photos?.length === 0 ? (
                <div className="py-6 text-center bg-muted/30 rounded-lg border border-border">
                  <Icon name="ImageOff" size={24} className="text-muted-foreground mx-auto mb-2" />
                  <p className="text-xs text-muted-foreground">No photos uploaded</p>
                </div>
              ) : (
                <div>
                  <p className="text-xs text-muted-foreground mb-2">{photos?.length} photo{photos?.length !== 1 ? 's' : ''}</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {photos?.map(photo => (
                      <div key={photo?.id} className="space-y-1">
                        <div
                          className="aspect-square rounded-lg overflow-hidden border border-border bg-muted cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => photo?.signedUrl && setLightboxUrl(photo?.signedUrl)}
                        >
                          {photo?.signedUrl ? (
                            <img src={photo?.signedUrl} alt="Task photo" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Icon name="Image" size={24} className="text-muted-foreground" />
                            </div>
                          )}
                        </div>
                        <p className="text-[10px] text-muted-foreground truncate">{photo?.uploader?.full_name || 'Unknown'}</p>
                        <p className="text-[10px] text-muted-foreground">{photo?.uploaded_at ? new Date(photo?.uploaded_at)?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : ''}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxUrl && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 p-4" onClick={() => setLightboxUrl(null)}>
          <button className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors" onClick={() => setLightboxUrl(null)}>
            <Icon name="X" size={20} className="text-white" />
          </button>
          <img src={lightboxUrl} alt="Full size" className="max-w-full max-h-full object-contain rounded-lg" />
        </div>
      )}
    </>
  );
};

export default TaskDetailModal;
